import "./procesos.css";
import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import LoadingOverlay from "../0. Utilidades/LoadingOverlay";
import { SiMicrosoftexcel } from "react-icons/si";

function ProcesoList() {
  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCurso, setSelectedCurso] = useState("");

  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_BASE_API}api/seleccion/entrevista_with_postulantes/`;
    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setProcesos(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      });
  }, []);

  const cursos = useMemo(() => {
    return [...new Set(procesos.map((proceso) => proceso.curso_comuna))];
  }, [procesos]);

  const filteredProcesos = useMemo(() => {
    if (!selectedCurso) return procesos;
    return procesos.filter((proceso) => proceso.curso_comuna === selectedCurso);
  }, [procesos, selectedCurso]);

  return (
    <div id="proceso-list">
      <h3>
        Psicoanálisis
        <a
          href={`${process.env.REACT_APP_BASE_API}api/seleccion/descargar_postulantes/`}
          rel="noopener noreferrer"
        >
          <SiMicrosoftexcel style={{ marginLeft: "10px", cursor: "pointer" }} />
        </a>
      </h3>
      <h6>
        Esta sección muestra todas las mujeres que pasaron por la entrevista con
        la coordinadora y ahora deben ser psicoanalizadas por la psicóloga.
      </h6>

      <div className="mb-3">
        <label htmlFor="curso-select">Seleccionar Comuna:</label>
        <select
          id="curso-select"
          className="form-control"
          value={selectedCurso}
          onChange={(e) => setSelectedCurso(e.target.value)}
        >
          <option value="">Todas las Comunas</option>
          {cursos.map((curso, index) => (
            <option key={index} value={curso}>
              {curso}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <LoadingOverlay />}
      {filteredProcesos.map((proceso) => (
        <ProcesoSection key={proceso.id} proceso={proceso} />
      ))}
    </div>
  );
}

function ProcesoSection({ proceso }) {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const removeDiacritics = (text) => {
    if (text == null) {
      return "";
    }
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const normalizedSearchQuery = removeDiacritics(searchQuery.toLowerCase());

  const filteredPostulantes = useMemo(() => {
    return proceso.postulantes.filter((postulante) => {
      const fullName = removeDiacritics(
        `${postulante.nombre} ${postulante.apellido}`.toLowerCase(),
      );
      const comuna = removeDiacritics(postulante.comuna?.toLowerCase());
      const rut = removeDiacritics(postulante.rut?.toLowerCase());

      return (
        fullName.includes(normalizedSearchQuery) ||
        comuna.includes(normalizedSearchQuery) ||
        rut.includes(normalizedSearchQuery)
      );
    });
  }, [proceso.postulantes, normalizedSearchQuery]);

  const data = useMemo(() => filteredPostulantes, [filteredPostulantes]);
  const columns = useMemo(
    () => [
      {
        Header: "Nombre",
        accessor: "nombre",
        Cell: ({ row }) => (
          <div>
            {row.original.nombre} {row.original.apellido}
            {row.original.tipo && (
              <span className="maestra-de-la-comunidad">¡RedMaestra!</span>
            )}
          </div>
        ),
        id: "nombre",
      },
      {
        Header: "Comuna",
        accessor: "comuna",
        Cell: ({ row }) => <div>{row.original.comuna}</div>,
        id: "comuna",
      },
      {
        Header: "Acciones",
        Cell: ({ row }) => (
          <a
            href={`api/seleccion/editar_postulante_psicoanalisis/${row.original.id}/`}
            className="btn btn-primary"
          >
            Entrevistar
          </a>
        ),
        id: "acciones",
        disableSortBy: true,
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useSortBy,
    usePagination,
  );

  return (
    <div className="proceso-section">
      <h3>
        {proceso.curso_nombre} ({proceso.curso_comuna})
      </h3>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <div className="pagination">
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[5, 10, 20].map((size) => (
            <option key={size} value={size}>
              Mostrar {size}
            </option>
          ))}
        </select>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>
        <span>
          Página {pageIndex + 1} de {pageOptions.length}
        </span>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>
      </div>
      <table {...getTableProps()} className="table table-hover">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default ProcesoList;
